import { Box, Button, FormHelperText, Grid, Typography } from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { BentoBrand } from "schemas/dashboard";
import { Map } from "schemas/functions";

import Editor from "components/Editor";
import { extensions } from "components/Editor/utils";

import DraftLoader from "../EmailBrandForm/DraftLoader";
import styles from "../styles";

interface ImproveCurrentPitchProps {
  handleBack?: () => void;
  handleNext?: (onboardingForm?: Map) => void;
  handleSkip?: () => void;
  selectedBrand?: BentoBrand;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  loading?: boolean;
  fromVaFlow?: boolean;
  pitchEmailValidationVa?: string;
  setPitchEmailValidationVa?: Dispatch<SetStateAction<string | undefined>>;
}

const ImproveCurrentPitch = ({
  handleBack,
  handleNext,
  handleSkip,
  selectedBrand,
  onboardingForm,
  setOnboardingForm,
  loading = false,
  fromVaFlow = false,
  pitchEmailValidationVa = undefined,
  setPitchEmailValidationVa = undefined,
}: ImproveCurrentPitchProps) => {
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const [pitchEmailValidation, setPitchEmailValidation] = useState<
    string | undefined
  >();

  const pitchEditor = useEditor(
    {
      extensions: extensions(),
      content: onboardingForm?.pitchEmail,
      onBlur({ editor }) {
        setOnboardingForm((prev) => {
          return { ...prev, pitchEmail: editor.getHTML() };
        });
      },
      onUpdate({ editor }) {
        setPitchEmailValidation(undefined);
        if (setPitchEmailValidationVa) {
          setPitchEmailValidationVa(undefined);
        }
      },
    },
    [!!onboardingForm?.pitchEmail],
  ) as TipTapEditor;

  const signatureEditor = useEditor(
    {
      extensions: extensions({
        placeholderText: "Best, {Your Name}",
        hardBreaksOnly: true,
      }),
      content: onboardingForm?.signature,
      onUpdate({ editor }) {
        setOnboardingForm((prev) => {
          return { ...prev, signature: editor.getHTML() };
        });
      },
    },
    [!!onboardingForm?.signature],
  ) as TipTapEditor;

  const onSubmit = async () => {
    if (!currentOrg) {
      return;
    }
    if (!onboardingForm.pitchEmail || onboardingForm.pitchEmail === "<p></p>") {
      setPitchEmailValidation("Please fill out this field.");
      return;
    }
    if (handleNext) {
      handleNext(onboardingForm);
    }
  };

  useEffect(() => {
    if (onboardingForm?.signature !== profile?.signature) {
      setOnboardingForm((prev) => {
        return { ...prev, signature: profile?.signature || "" };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.signature]);

  const errorStr = pitchEmailValidation || pitchEmailValidationVa;

  return (
    <>
      {!fromVaFlow && (
        <Grid item xs={12} container alignItems="center">
          <Typography
            variant="h3"
            gutterBottom
            sx={{ mb: 3, textAlign: "center" }}
          >
            <Box
              component="i"
              color="secondary.dark"
              sx={{ pr: 2 }}
              className="fa-solid fa-paper-plane"
            />
            {loading
              ? selectedBrand
                ? `Generating a pitch to ${selectedBrand?.brandName}`
                : `Generating your pitch template`
              : "Your current pitch"}
          </Typography>
        </Grid>
      )}

      {loading && (
        <Box sx={styles.templateWrapper}>
          <Box sx={styles.loaderWrapper}>
            <DraftLoader />
          </Box>
        </Box>
      )}

      {!loading && (
        <>
          <Grid sx={styles.fieldContainer}>
            <FormHelperText error={!!errorStr} sx={styles.helperText}>
              Paste your current pitch email here (can be edited later) *
            </FormHelperText>
            <Editor
              editor={pitchEditor}
              allowAttachment={false}
              allowImage={true}
            />
            {errorStr && (
              <FormHelperText error={!!errorStr}>{errorStr}</FormHelperText>
            )}

            <Grid item xs={12}>
              <FormHelperText sx={styles.helperText}>Signature</FormHelperText>
              <Editor editor={signatureEditor} allowImage={true} />
            </Grid>
          </Grid>
          {!fromVaFlow && (
            <Grid container justifyContent="flex-start" sx={{ mt: 2 }}>
              <Grid item xs="auto">
                <Button onClick={handleBack} disableElevation color="secondary">
                  Back
                </Button>
              </Grid>
              <Grid item xs></Grid>
              {handleSkip && (
                <Grid item xs="auto">
                  <Button
                    onClick={handleSkip}
                    color="secondary"
                    disableElevation
                  >
                    Skip
                  </Button>
                </Grid>
              )}
              <Grid item xs="auto">
                <Button disableElevation onClick={onSubmit} variant="contained">
                  Next
                </Button>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default ImproveCurrentPitch;
